import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { articles, Article } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

function HotBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-500/15 text-primary-500 border border-primary-500/20 whitespace-nowrap">
      <span className="w-3 h-3 flex items-center justify-center">
        <i className="ri-fire-fill text-[10px]"></i>
      </span>
      HOT
    </span>
  );
}

function ExclusiveBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-accent-500/15 text-accent-500 border border-accent-500/20 whitespace-nowrap">
      <span className="w-3 h-3 flex items-center justify-center">
        <i className="ri-vip-crown-fill text-[10px]"></i>
      </span>
      独家
    </span>
  );
}

function ArticleCard({ article, variant = 'default' }: { article: Article; variant?: 'default' | 'featured' | 'compact' }) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative overflow-hidden rounded-xl bg-background-100 border border-background-300/40 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
      >
        <div className="relative w-full h-[280px] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {article.isHot && <HotBadge />}
              {article.isExclusive && <ExclusiveBadge />}
              <span className="text-xs text-foreground-500 bg-background-900/60 px-2 py-0.5 rounded-md">
                {article.categoryName}
              </span>
            </div>
            <h3 className="text-lg font-bold text-white mb-2 line-clamp-2 leading-snug group-hover:text-primary-300 transition-colors">
              {article.title}
            </h3>
            <p className="text-sm text-foreground-400 line-clamp-2 mb-3">{article.excerpt}</p>
            <div className="flex items-center gap-4 text-xs text-foreground-600">
              <span className="flex items-center gap-1">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-eye-line"></i>
                </span>
                {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-chat-3-line"></i>
                </span>
                {article.commentCount}
              </span>
              <span className="ml-auto text-foreground-700">{article.publishedAt.split('T')[0]}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-100/50 transition-colors cursor-pointer"
      >
        <div className="shrink-0 w-24 h-16 rounded-lg overflow-hidden bg-background-200">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-200 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-600">
            <span>{article.categoryName}</span>
            <span className="flex items-center gap-0.5">
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-eye-line text-[10px]"></i>
              </span>
              {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block bg-background-100 border border-background-300/40 rounded-xl overflow-hidden hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
    >
      <div className="relative w-full h-[180px] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 flex items-center gap-1.5 flex-wrap">
          {article.isHot && <HotBadge />}
          {article.isExclusive && <ExclusiveBadge />}
        </div>
      </div>
      <div className="p-4">
        <span className="text-xs text-primary-500 font-medium">{article.categoryName}</span>
        <h3 className="text-sm font-semibold text-foreground-200 line-clamp-2 mt-1.5 leading-snug group-hover:text-primary-500 transition-colors">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-600 line-clamp-2 mt-2">{article.excerpt}</p>
        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-700">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-eye-line text-[10px]"></i>
            </span>
            {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-chat-3-line text-[10px]"></i>
            </span>
            {article.commentCount}
          </span>
          <span className="ml-auto">{article.publishedAt.split('T')[0]}</span>
        </div>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const [animatedIn, setAnimatedIn] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimatedIn(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const sorted = [...articles].sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  const hotArticles = sorted.filter((a) => a.isHot).slice(0, 5);
  const featuredArticle = sorted[0];
  const latestArticles = sorted.slice(1, 9);
  const exclusiveArticles = sorted.filter((a) => a.isExclusive).slice(0, 4);

  return (
    <>
      {/* SEO Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: '91黑料',
              url: 'https://91heilia.com/',
              description: '91黑料是专注于热门爆料、真实内幕与独家黑料内容的资讯聚合平台。',
              address: {
                '@type': 'PostalAddress',
                addressLocality: '北京',
                addressRegion: '北京市',
                addressCountry: 'CN',
              },
              contactPoint: {
                '@type': 'ContactPoint',
                telephone: '400-888-9191',
                contactType: 'customer service',
              },
            },
            {
              '@context': 'https://schema.org',
              '@type': 'WebSite',
              url: 'https://91heilia.com/',
              name: '91黑料',
              description: '最新爆料资讯聚合平台，黑料不打烊',
              potentialAction: {
                '@type': 'SearchAction',
                target: 'https://91heilia.com/search?q={search_term_string}',
                'query-input': 'required name=search_term_string',
              },
            },
            {
              '@context': 'https://schema.org',
              '@type': 'WebPage',
              url: 'https://91heilia.com/',
              name: '91黑料 - 最新爆料资讯聚合平台',
              description: '91黑料是专注于热门爆料、真实内幕与独家黑料内容的资讯聚合平台。',
              isPartOf: { '@type': 'WebSite', name: '91黑料', url: 'https://91heilia.com/' },
            },
          ]),
        }}
      />

      {/* Hero Section */}
      <section className="relative w-full min-h-[600px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://readdy.ai/api/search-image?query=Dark%20mysterious%20abstract%20geometric%20background%20with%20deep%20shadows%2C%20dramatic%20light%20rays%20from%20above%2C%20cinematic%20atmosphere%2C%20dark%20charcoal%20and%20black%20tones%20with%20subtle%20crimson%20accents%2C%20modern%20minimal%20style%2C%20no%20text%2C%20no%20people%2C%20abstract%20and%20artistic&width=1600&height=900&seq=hero-bg-91hl&orientation=landscape)',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-50"></div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <div
            className={`transition-all duration-700 transform ${
              animatedIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 mb-6">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-fire-fill text-primary-500 text-sm"></i>
              </span>
              <span className="text-sm text-primary-500 font-medium">黑料不打烊 · 每日必看</span>
            </div>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-black text-white leading-tight">
              <span className="text-primary-500">91黑料</span>
              <br />
              最新爆料资讯聚合平台
            </h1>
            <p className="mt-5 text-base md:text-lg text-foreground-300 max-w-2xl mx-auto leading-relaxed">
              专注于热门爆料、真实内幕与独家黑料内容。第一时间获取最新91黑料动态，挖掘真实料源，了解圈内不为人知的内幕信息，内容持续更新。
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="#latest"
                className="px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看最新爆料
              </Link>
              <Link
                to="#categories"
                className="px-6 py-3 rounded-full bg-background-100/60 border border-background-400/30 text-foreground-200 text-sm font-medium hover:bg-background-100/80 transition-colors whitespace-nowrap cursor-pointer"
              >
                浏览分类
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Quick Nav */}
      <section id="categories" className="max-w-7xl mx-auto px-4 md:px-6 -mt-10 relative z-20">
        <div className="bg-background-100 border border-background-300/40 rounded-2xl p-5">
          <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-background-200/50 transition-colors cursor-pointer group"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500 group-hover:bg-primary-500 group-hover:text-white transition-all">
                  <i className={`${cat.icon} text-lg`}></i>
                </span>
                <span className="text-xs font-medium text-foreground-400 group-hover:text-foreground-100 transition-colors whitespace-nowrap">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Article */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 mt-10">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-foreground-100 font-heading">
            <a href="#!">今日重磅</a>
          </h2>
        </div>
        <ArticleCard article={featuredArticle} variant="featured" />
      </section>

      {/* Hot + Latest Two Column */}
      <section id="latest" className="max-w-7xl mx-auto px-4 md:px-6 mt-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Hot Articles */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-fire-fill text-primary-500"></i>
              </span>
              <h2 className="text-lg font-bold text-foreground-100 font-heading">
                <a href="#!">热门爆料</a>
              </h2>
            </div>
            <div className="bg-background-100 border border-background-300/40 rounded-xl p-2 divide-y divide-background-300/30">
              {hotArticles.map((article) => (
                <ArticleCard key={article.id} article={article} variant="compact" />
              ))}
            </div>
          </div>

          {/* Latest Articles */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-foreground-100 font-heading">
                <a href="#!">最新资讯</a>
              </h2>
              <Link
                to="/category/yule-baoliao"
                className="text-xs text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer flex items-center gap-1"
              >
                查看全部
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-[10px]"></i>
                </span>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {latestArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Exclusive Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 mt-10 mb-14">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-vip-crown-fill text-accent-500"></i>
          </span>
          <h2 className="text-lg font-bold text-foreground-100 font-heading">
            <a href="#!">独家爆料</a>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {exclusiveArticles.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 mb-14">
        <div className="bg-background-100 border border-background-300/40 rounded-2xl p-6 md:p-8">
          <h2 className="text-xl font-bold text-foreground-100 font-heading text-center mb-6">
            <a href="#!">常见问题</a>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {[
              { q: '91黑料是什么？', a: '91黑料是一个专注于热门爆料、真实内幕与独家黑料内容的资讯聚合平台，提供最新、最全的爆料资讯。' },
              { q: '91黑料的内容来源是什么？', a: '我们的内容来自多方渠道，包括匿名爆料人、深度调查团队以及各行业的内部知情人士。所有内容在发布前均经过严格核实。' },
              { q: '如何向91黑料投稿？', a: '您可以通过网站底部的联系方式或发送邮件至tips@91heilia.com进行投稿。我们将严格保护爆料人的隐私信息。' },
              { q: '91黑料的内容多久更新一次？', a: '我们保持每日更新，确保读者能够第一时间获取最新爆料资讯。建议您将91黑料加入收藏，每日必看。' },
            ].map((faq, i) => (
              <div key={i} className="p-4 rounded-lg bg-background-50/50 border border-background-300/20">
                <h4 className="text-sm font-semibold text-foreground-200 mb-2">{faq.q}</h4>
                <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                { '@type': 'Question', name: '91黑料是什么？', acceptedAnswer: { '@type': 'Answer', text: '91黑料是一个专注于热门爆料、真实内幕与独家黑料内容的资讯聚合平台，提供最新、最全的爆料资讯。' } },
                { '@type': 'Question', name: '91黑料的内容来源是什么？', acceptedAnswer: { '@type': 'Answer', text: '我们的内容来自多方渠道，包括匿名爆料人、深度调查团队以及各行业的内部知情人士。所有内容在发布前均经过严格核实。' } },
                { '@type': 'Question', name: '如何向91黑料投稿？', acceptedAnswer: { '@type': 'Answer', text: '您可以通过网站底部的联系方式或发送邮件至tips@91heilia.com进行投稿。我们将严格保护爆料人的隐私信息。' } },
                { '@type': 'Question', name: '91黑料的内容多久更新一次？', acceptedAnswer: { '@type': 'Answer', text: '我们保持每日更新，确保读者能够第一时间获取最新爆料资讯。建议您将91黑料加入收藏，每日必看。' } },
              ],
            }),
          }}
        />
      </section>
    </>
  );
}